<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef, watch } from "vue";

import PlotlyWidget from "@/components/datascience/PlotlyWidget.vue";
import CopyableElement from "@/components/ui/CopyableElement.vue";
import ExpandableCard from "@/components/ui/ExpandableCard.vue";
import FloatingTooltip from "@/components/ui/FloatingTooltip.vue";
import MarkdownWidget from "@/components/ui/MarkdownWidget.vue";
import SimpleButton from "@/components/ui/SimpleButton.vue";
import {
  getReportName,
  MediaType,
  type CrossValidationReportDto,
  type EstimatorReportDto,
} from "@/dto";
import { useClipboard } from "@/services/clipboard";
import {
  makeBarplot,
  makeBoxPlot,
  type FeatureImportanceDataframe,
} from "@/services/featureImportanceHelpers";
import { useDatasourceStore } from "@/stores/datasource";

const { report, tracePrefix = "" } = defineProps<{
  report: EstimatorReportDto | CrossValidationReportDto;
  tracePrefix?: string;
}>();

const clipboard = useClipboard();
const datasource = useDatasourceStore();

const expandableCard = useTemplateRef<InstanceType<typeof ExpandableCard>>("expandableCard");
const html = ref("");

const featureImportanceDataframes = ref<FeatureImportanceDataframe[]>([]);
const hasPermutation = ref(false);

const permutationSnippet = `# report refers to your skore-lib Report instance
report.feature_importance.permutation(data_source="train")
report.feature_importance.permutation(data_source="test")

# project refers to a skore Project instance
project.put("my report name", report)`;
const permutationSnippetMarkdown = computed(() => "```python\n" + permutationSnippet + "\n```");

const reportMediaHtml = computed(() =>
  report.medias.find((r) => r.category === "model" && r.content_type === "text/html")
);

const featureImportancePlotSpecs = computed(() => {
  return new Map(
    featureImportanceDataframes.value
      .filter((df) => df.datasource === datasource.sourceName || df.datasource === null)
      .map((df) => {
        let title = "";
        if (df.method === "permutation") {
          title = "Permutation Feature Importance";
        } else if (df.method === "coefficients") {
          title = "Model Coefficients";
        } else if (df.method === "mean_decrease_impurity") {
          title = "Mean Decrease in Impurity (MDI)";
        }

        // Enrich with reportName if available
        const reportName = getReportName(report);
        if (reportName) {
          title = `${title} — ${reportName}`;
        }

        let xAxisLabel = "";
        if (report.ml_task.includes("classification")) {
          xAxisLabel = "Decrease in accuracy";
        } else if (report.ml_task.includes("regression")) {
          xAxisLabel = "Decrease in R²";
        } else {
          // case "unknown" in typing
          xAxisLabel = "Score decrease";
        }

        return [
          df.verboseMethodName,
          df.method === "permutation"
            ? makeBoxPlot([df], title, xAxisLabel, tracePrefix)
            : makeBarplot([df], title, xAxisLabel),
        ];
      })
  );
});

async function fetchHtml() {
  if (reportMediaHtml.value) {
    const response = await fetch(reportMediaHtml.value.presigned_url);
    html.value = await response.text();
  }
}

async function fetchFeatureImportanceDataframes() {
  if (!report || !Array.isArray(report.medias)) {
    return;
  }

  const promises = report.medias
    .filter((m) => m.category === "feature_importance" && m.content_type === MediaType.DataFrame)
    .map(async (m) => {
      const response = await fetch(m.presigned_url);
      const representation = await response.json();
      return {
        method: m.method,
        verboseMethodName: m.verbose_name.replace("Feature importance - ", ""),
        indexNames: representation.index_names,
        datasource: m.data_source,
        ...representation,
      } as FeatureImportanceDataframe;
    });

  const fetchedDataframes = await Promise.all(promises);
  hasPermutation.value = fetchedDataframes.some((df) => df.method === "permutation");
  featureImportanceDataframes.value = fetchedDataframes;
}

async function copyPermutationSnippet() {
  await clipboard.copyText(permutationSnippet);
}

function expand() {
  expandableCard.value?.expand();
}

watch(
  () => report,
  async () => {
    html.value = "";
    featureImportanceDataframes.value = [];
    await fetchHtml();
    await fetchFeatureImportanceDataframes();
  }
);

onMounted(async () => {
  await fetchHtml();
  await fetchFeatureImportanceDataframes();
});

defineExpose({ expand });
</script>

<template>
  <ExpandableCard ref="expandableCard" title="Model pipeline" icon="icon-divider">
    <template #actions></template>

    <div class="pipeline">
      <CopyableElement>
        <div v-html="html" />
      </CopyableElement>
    </div>

    <template #expandableContent>
      <div id="feature-importance" class="card-content feature-importance">
        <CopyableElement
          v-for="[name, plotSpec] in featureImportancePlotSpecs.entries()"
          :id="`feature-importance-${name.toLowerCase()}`"
          :key="name"
        >
          <PlotlyWidget :spec="plotSpec" />
        </CopyableElement>
        <div v-if="!hasPermutation" class="alert text-base-regular">
          <header class="text-base-regular">
            <i class="icon icon-info-circle-2" />
            Permutation feature importance has not been computed yet for this report. Run the
            following code to compute it now:
          </header>

          <div class="alert-body">
            <div class="alert-content">
              <div class="code-snippet">
                <div class="actions">
                  <FloatingTooltip text="Copy to clipboard">
                    <SimpleButton
                      icon-left="icon-copy"
                      kind="ghost"
                      @click="copyPermutationSnippet"
                    />
                  </FloatingTooltip>
                </div>

                <MarkdownWidget :source="permutationSnippetMarkdown" />
              </div>
            </div>

            <div class="alert-footer">
              <span class="label-date">Coming soon</span>: We are working on automatic computation
              so you don't have to trigger it manually in future versions.
            </div>
          </div>
        </div>
      </div>
    </template>
  </ExpandableCard>
</template>

<style scoped>
:deep(.card-content:has(.pipeline)) {
  overflow: hidden;
  padding: 0;
}

.pipeline {
  background-image: radial-gradient(var(--content-disabled) 1px, transparent 0);
  background-position: -6px -7px;
  background-size: 13px 15px;

  .copyable-element {
    display: flex;
    justify-content: center;
    padding: var(--numbers-18);
    border: 0;
  }
}

.feature-importance {
  display: flex;
  flex-direction: column;
  gap: var(--numbers-8) var(--numbers-16);

  .copyable-element {
    overflow: hidden;
    width: 100%;
    min-width: 0;
    border: solid var(--stroke-width-md) var(--border-tertiary);
    border-radius: var(--numbers-6);
  }

  header {
    display: flex;
    align-items: center;
    gap: var(--numbers-8);
  }

  .alert {
    display: flex;
    flex-direction: column;
    padding: var(--numbers-12);
    gap: var(--numbers-12);

    .alert-body {
      .alert-content {
        .code-snippet {
          position: relative;
          padding: var(--numbers-12);
          border: var(--stroke-width-md) solid var(--border-tertiary);
          border-radius: var(--numbers-6);
          background: var(--background-secondary);

          .actions {
            position: absolute;
            top: var(--numbers-8);
            right: var(--numbers-8);
          }
        }
      }
    }
  }

  .feature-importance-plot {
    padding: var(--numbers-12);
  }
}

:deep(.feature-importance .copyable-element .element) {
  width: 100%;
  min-width: 0;
}
</style>
